import React from 'react';
import { Platform, View } from 'react-native';

import { PresenceGlowShell } from '@/common/components/presence/PresenceGlowShell.web';
import { NebulaOrbStatic } from '@/common/components/presence/nebula/NebulaOrbStatic';
import { NebulaOrbWeb } from '@/common/components/presence/nebula/NebulaOrb.web';
import type { AIPresenceOrbProps } from '@/common/components/presence/types';
import { useOrbPalette } from '@/common/components/presence/useOrbPalette';
import { useOrbReducedMotion } from '@/common/components/presence/useOrbReducedMotion';
import {
  resolvePresenceVisualState,
  usePresenceSpeechEnvelope,
} from '@/common/components/presence/usePresenceSpeechEnvelope';

/**
 * Shared Deep-Field Nebula AI presence — one material for tutor and oral exam.
 */
export function AIPresenceOrb(props: AIPresenceOrbProps) {
  const {
    state,
    size,
    activityPulse = 0,
    trackRef,
    speechEnvelopeOverride,
    testID = 'ai-presence-orb',
  } = props;
  const reducedMotion = useOrbReducedMotion();
  const palette = useOrbPalette();
  const useStatic = reducedMotion || Platform.OS !== 'web';
  const { amp, punch } = usePresenceSpeechEnvelope({
    state,
    trackRef,
    reducedMotion,
    envelopeOverride: speechEnvelopeOverride,
  });
  const visualState = resolvePresenceVisualState(state, punch);

  const nebula = useStatic ? (
    <NebulaOrbStatic palette={palette} state={visualState} size={size} />
  ) : (
    <NebulaOrbWeb
      palette={palette}
      state={visualState}
      size={size}
      activityPulse={activityPulse}
      speechAmp={amp}
      speechPunch={punch}
    />
  );

  const orbVisual = Platform.OS === 'web' ? (
    <PresenceGlowShell
      state={visualState}
      size={size}
      palette={palette}
      punch={punch}
      reducedMotion={reducedMotion}
      testID={`${testID}-glow`}
    >
      {nebula}
    </PresenceGlowShell>
  ) : nebula;

  return (
    <View
      testID={testID}
      aria-hidden
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    >
      {orbVisual}
    </View>
  );
}
